/**
 * One customer as `masters.get_customers` knows them — and their billing address, as text.
 *
 * ---- Two readers of `get_customers` ----
 *
 * `useCustomerSearch` is the picker every customer field uses (scoped to a company — the
 * unscoped `/api/resource/Customer` picker it replaced listed the whole ERP). `useCustomerRecord`
 * is the quotation page's read of ONE customer for their ADDRESS (Akhil, 2026-09-14: "pass the company and customer name, get the customer
 * address"), and `get_customers?search=<customer>&company=<company>` is where the server answers
 * it — `company` sent on every call, as it is now required to be.
 *
 * ---- The row is POSITIONAL ----
 *
 * Measured 2026-09-14:
 *
 *     ["AKHIL","AKHIL",null,null,"AED","AKHIL",null,"akhilnair43@gmail.com-Billing","akhilnair43@gmail.com"]
 *       0 name  1 customer_name  2 group  3 territory  4 currency  5 platform_id  6 ?
 *       7 customer_primary_address (an `Address` DOCNAME)  8 email_id
 *
 * No keys, and the Postman entry carries no example, so the column meanings are inferred from
 * the values and pinned by test. A keyed row is accepted too, so the day the method names its
 * columns nothing here breaks.
 *
 * ---- The address is a docname, not text ----
 *
 * Column 7 is the NAME of an `Address` document. The quotation's own `address_display` stays
 * `null` even after `customer_address` is written (measured), so the text has to be read from
 * the `Address` doc through `/api/resource` — one more read doctype on the allowlist. Filed as
 * an ask: `address_display` on `get_quotation` would remove this second read.
 */

import { useQuery } from '@tanstack/react-query'
import { callGet } from './bosun'
import { getResourceDoc } from './frappe'
import { str } from './wire'
import { getCompany } from './company'
import type { PickerOption } from './useEnquiryFromMail'

export interface CustomerRecord {
  name: string
  customer_name: string
  /** The customer's own default currency, `''` when none on file. */
  currency: string
  /** The `Address` docname of their primary address, `''` when none. */
  address_name: string
  email: string
}

export function toCustomerRecord(row: unknown): CustomerRecord | null {
  let record: CustomerRecord
  if (Array.isArray(row)) {
    const at = (i: number) => str(row[i])
    record = { name: at(0), customer_name: at(1) || at(0), currency: at(4), address_name: at(7), email: at(8) }
  } else if (row && typeof row === 'object') {
    const r = row as Record<string, unknown>
    record = {
      name: str(r.name),
      customer_name: str(r.customer_name) || str(r.name),
      currency: str(r.default_currency),
      address_name: str(r.customer_primary_address),
      email: str(r.email_id),
    }
  } else {
    return null
  }
  return record.name === '' ? null : record
}

/* ============ the customer PICKER — get_customers, scoped to a company ============ */

/**
 * Picker options over `get_customers`. Replaces the `/api/resource/Customer` picker (2026-09-15):
 * that one had no company scope and listed every customer in the ERP — "all customers coming"
 * — where `get_customers?company=` answers only the company's own, and `company` is mandatory
 * on it now (Akhil, 5d). The currency on file rides along as the second line, since it is what
 * the quotation will default to.
 */
export function toCustomerOptions(wire: unknown): PickerOption[] {
  const rows = (wire as { data?: unknown } | null)?.data
  if (!Array.isArray(rows)) return []
  return rows
    .map(toCustomerRecord)
    .filter((r): r is CustomerRecord => r !== null)
    .map((r) => ({ name: r.name, label: r.customer_name, sublabel: r.currency || undefined }))
}

/**
 * `LinkCombo`'s `useOptions` shape, for one company. Call sites close over their company —
 * the enquiry's, or the one being picked on a create dialog — exactly as `useSalesPeople` is
 * scoped. An empty company falls back to the session's operating company (never empty), so a
 * dialog whose company is not yet chosen still searches somewhere rather than nowhere.
 */
export function useCustomerSearch(search: string, company: string | null | undefined): { data?: PickerOption[]; isPending: boolean } {
  const scope = (company ?? '').trim() || getCompany()
  const q = search.trim()
  const query = useQuery({
    queryKey: ['customer-picker', scope, q] as const,
    queryFn: async () => toCustomerOptions(await callGet<unknown>('masters', 'get_customers', { search: q, company: scope, limit: 50 })),
    staleTime: 5 * 60_000,
    // Keeps the list on screen while a new search runs — same treatment as `useSuppliers`.
    placeholderData: (previous) => previous,
  })
  return { data: query.data, isPending: query.isPending }
}

/** The customer's record, found by exact name among the search results for it. */
export function useCustomerRecord(customer: string | null | undefined, company: string | null | undefined) {
  const name = (customer ?? '').trim()
  const scope = (company ?? '').trim()
  return useQuery({
    queryKey: ['customer-record', name, scope] as const,
    queryFn: async (): Promise<CustomerRecord | null> => {
      const res = await callGet<{ data?: unknown[] }>('masters', 'get_customers', { search: name, company: scope, limit: 20 })
      const rows = (res?.data ?? []).map(toCustomerRecord)
      // `search` is a contains-match: "AKHIL" also finds "AKHIL MARINE". The exact name wins.
      return rows.find((r) => r?.name === name) ?? rows.find((r) => r !== null) ?? null
    },
    enabled: name !== '',
    staleTime: 5 * 60_000,
  })
}

export interface WireAddress {
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  pincode?: string | null
}

/** The address as it prints: one line per part, city and pincode together, blanks skipped. */
export function formatAddress(a: WireAddress): string {
  const cityLine = [str(a.city), str(a.pincode)].filter((p) => p !== '').join(' ')
  return [str(a.address_line1), str(a.address_line2), cityLine, str(a.state), str(a.country)]
    .filter((line) => line !== '')
    .join('\n')
}

/** The text of one `Address` doc, `''` when there is none to read. */
export function useAddressText(addressName: string | null | undefined) {
  const name = (addressName ?? '').trim()
  return useQuery({
    queryKey: ['address-text', name] as const,
    queryFn: async () => formatAddress(await getResourceDoc<WireAddress>('Address', name)),
    enabled: name !== '',
    staleTime: 30 * 60_000,
  })
}
